/* One remediation plan: what it would change, what it cannot, and the next step it is waiting on.
 *
 * **Laid out as the design draws it, top to bottom in the order a reader decides.** The plan's table
 * and state, one sentence for what the state means, what an approver is signing for, the controls
 * and the decision, what executing it would mean, the script, the proposals, and then the record —
 * the plan's facts, its artifact and its lifecycle — side by side at the foot.
 *
 * **Every cell in the Proposals table is mechanism, never a value** — which transformation, which
 * source, which citation. Nothing on this screen shows what is in a row, and there is no field in
 * the payload a customer value would fit in.
 *
 * **The assessment is a button, not something a page load does.** `POST /execution-assessment`
 * dials a connection to the customer's production database and counts rows in it — a rung-2 read
 * somebody has to have decided to make, and the platform gates the route on `execute` rather than
 * `read` for exactly that reason. The script is behind a control for the sibling reason: handing over
 * SQL should be something somebody asked for.
 *
 * **There is no Execute control here or anywhere.** No write adapter exists, so a button could only
 * fail on click. Its absence is the honest answer.
 */

import { useId, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import type { ExecutionAssessment, Plan } from '@/shared/api/generated/remediation';
import { downloadFile } from '@/shared/api/http';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SCREEN_PATHS } from '@/shared/routes';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useDataset, useSchemaHistory } from '@/features/catalog-browser';

import { useAssessExecution } from '../api/execution';
import {
  artifactUrl,
  usePlan,
  usePlanAction,
  usePlanArtifacts,
  useScriptPreview,
} from '../api/plans';
import {
  asInstant,
  blockedFor,
  decisionNotice,
  factsOf,
  hasArtifact,
  isApplicable,
  lifecycleOutline,
  offeredFor,
  proposalRows,
  relationOf,
  signingLines,
  stateLabelKey,
  stateToneOf,
  tierLabelKey,
  toneOf,
  unfixableCount,
} from '../model/plans';
import type { Fact, PlanAction } from '../model/plans';

/** A refusal's tone. `no_write_adapter` is `info`, not a failure: nothing is wrong with the plan.
 *
 * `refusal` arrives as `string` rather than a union — the contract's enum lists `null` among its
 * members, so the generator widens it — which means a server one version ahead sending an eighth
 * reason is genuinely reachable. It falls back to `warn` rather than to the permitted tone, which is
 * the mistake §9 records `verdictTone`'s `default:` making in `core.ai.evals`.
 */
const REFUSAL_TONE: Readonly<Record<string, 'warn' | 'info' | 'bad'>> = {
  not_approved: 'warn',
  wrong_state: 'info',
  not_simulated: 'warn',
  unsafe_tier: 'bad',
  not_reversible: 'bad',
  out_of_region: 'bad',
  no_write_adapter: 'info',
};

/** What an action did, said in place once it has been accepted. */
interface Done {
  readonly action: PlanAction;
}

/** A titled card, the one frame every section on this page sits in. */
function Card({
  title,
  action,
  table = false,
  children,
}: {
  readonly title: string;
  readonly action?: ReactNode;
  readonly table?: boolean;
  readonly children: ReactNode;
}): ReactElement {
  const headingId = useId();
  return (
    <section
      className={table ? 'lq-cp__card lq-cp__card--table' : 'lq-cp__card lq-rm__card'}
      aria-labelledby={headingId}
    >
      <div className={table ? 'lq-cp__cardHead' : 'lq-rm__cardHead'}>
        <h2 id={headingId} className="lq-cp__cardTitle">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** "transactions.merchant_id" — the table and the column, which is what a reader scans for. The
 * schema-qualified name stays on hover, because two schemas can hold a table of the same name. */
function targetOf(qualifiedName: string, column: string): string {
  const table = qualifiedName.split('.').pop() ?? qualifiedName;
  return `${table}.${column}`;
}

function ProposalsTable({
  plan,
  assessment,
}: {
  readonly plan: Plan;
  readonly assessment: ExecutionAssessment | null;
}): ReactElement {
  const { t } = useTranslation();
  /* Keyed on the proposal id, so a preview lands against its own row rather than against whichever
     row happened to share an index. The assessment's preview list is only the applicable ones. */
  const previews = new Map(
    (assessment?.previews ?? []).map((preview) => [preview.proposal_id, preview]),
  );

  return (
    <div className="lq-cp__scroll">
      <table className="lq-table lq-table--ledger lq-cp__table lq-rm__proposals">
        <caption className="lq-visually-hidden">
          {t('remediation.proposalsCaption', { plan: plan.id })}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t('remediation.column.target')}</th>
            <th scope="col">{t('remediation.column.tier')}</th>
            <th scope="col">{t('remediation.column.role')}</th>
            <th scope="col">{t('remediation.column.wouldDo')}</th>
            <th scope="col">{t('remediation.column.wouldChange')}</th>
            {/* Only an assessment measures this, so the column appears once one has run. Before
                then every cell would read "Not assessed", which is a column of nothing. */}
            {assessment === null ? null : (
              <th scope="col" title={t('remediation.unresolvedHint')}>
                {t('remediation.column.unresolved')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {plan.proposals.map((proposal) => {
            const preview = previews.get(proposal.id);
            const rows = proposalRows(proposal);
            const action = proposal.action;
            return (
              <tr key={proposal.id}>
                <td
                  data-label={t('remediation.column.target')}
                  className="lq-mono lq-rm__target"
                  title={`${proposal.qualified_name}.${proposal.column}`}
                >
                  {targetOf(proposal.qualified_name, proposal.column)}
                </td>
                <td data-label={t('remediation.column.tier')} className="lq-nowrap">
                  <span className={`lq-tag lq-tag--rmtier-${toneOf(proposal)}`}>
                    {t('remediation.tierBadge', {
                      tier: proposal.tier,
                      meaning: t(tierLabelKey(proposal.tier)),
                    })}
                  </span>
                </td>
                <td
                  data-label={t('remediation.column.role')}
                  className="lq-nowrap"
                  title={t(`remediation.roleNote.${proposal.column_role}`)}
                >
                  {t(`remediation.role.${proposal.column_role}`)}
                </td>
                {/* Mechanism only, with the rationale on hover. A Tier D row has no action at all,
                    which is why it says so rather than showing a blank that reads as a rendering
                    fault. */}
                <td
                  data-label={t('remediation.column.wouldDo')}
                  title={proposal.rationale}
                >
                  {action === null ? (
                    <span className="lq-muted">{t('remediation.noDerivation')}</span>
                  ) : (
                    <>
                      {t(`remediation.operation.${action.operation}`)}
                      <span className="lq-cp__muted">
                        {' · '}
                        {t(`remediation.source.${action.value_source}`)}
                        {/* `?? null` rather than `=== null`: the field is optional on the wire, so
                            an absent one is `undefined` and a null check alone would put the word
                            "undefined" on screen beside a customer's column name. */}
                        {(action.reference ?? null) === null
                          ? ''
                          : ` · ${String(action.reference)}`}
                      </span>
                    </>
                  )}
                </td>
                <td
                  data-label={t('remediation.column.wouldChange')}
                  className="lq-nowrap"
                >
                  {!isApplicable(proposal) ? (
                    <span className="lq-muted">{t('remediation.never')}</span>
                  ) : rows.state === 'unsimulated' ? (
                    <span className="lq-muted">{t('remediation.notSimulated')}</span>
                  ) : (
                    rows.rows.toLocaleString()
                  )}
                </td>
                {assessment === null ? null : (
                  <td
                    data-label={t('remediation.column.unresolved')}
                    className="lq-nowrap"
                  >
                    {/* A row the assessment did not preview has nothing to report; a zero there
                        would say the fix covers every flagged row. */}
                    {preview === undefined ? (
                      <span className="lq-muted">{t('remediation.notAssessed')}</span>
                    ) : (
                      <span
                        className={
                          preview.rows_unresolved > 0 ? 'lq-rm__unresolved' : undefined
                        }
                      >
                        {preview.rows_unresolved.toLocaleString()}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** What executing this plan would mean, measured only when somebody asks.
 *
 * **Before an assessment the card says so and offers it**, rather than drawing three tiles of which
 * two would be guesses. After one, the platform's verdict comes first — whether a write would proceed
 * and, if not, why — and the three figures an approver weighs sit under it.
 */
function AssessmentCard({
  plan,
  assessment,
  busy,
  failure,
  onAssess,
}: {
  readonly plan: Plan;
  readonly assessment: ExecutionAssessment | null;
  readonly busy: boolean;
  readonly failure: string | null;
  readonly onAssess: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const flagged = unfixableCount(plan);

  return (
    <Card title={t('remediation.ifExecuted')}>
      {busy ? (
        <p className="lq-rm__line" role="status">
          {t('remediation.assess.running')}
        </p>
      ) : assessment === null ? (
        <>
          <p className="lq-rm__line">{t('remediation.assess.notYet')}</p>
          <button
            type="button"
            className="lq-rm__act lq-rm__act--quiet"
            onClick={onAssess}
          >
            {t('remediation.assess.run')}
          </button>
        </>
      ) : (
        <>
          <div
            className={`lq-tintband lq-tintband--${
              assessment.refusal === null
                ? 'info'
                : (REFUSAL_TONE[assessment.refusal] ?? 'warn')
            }`}
          >
            <div className="lq-tintband__text">
              <strong>
                {assessment.refusal === null
                  ? t('remediation.wouldProceed')
                  : t(`execution.refusal.${assessment.refusal}`)}
              </strong>{' '}
              {/* The platform's own `detail`, verbatim. Restating it here would be a second place
                  deciding what a refusal means, and the two would drift. */}
              {assessment.detail}
              {assessment.terminal ? ` ${t('remediation.terminalNote')}` : ''}{' '}
              {t('remediation.assessedAt', { when: asInstant(assessment.assessed_at) })}
            </div>
          </div>
          <div className="lq-rm__tiles">
            <div className="lq-rm__tile">
              <div className="lq-rm__tileKey">{t('remediation.stat.rows')}</div>
              <div className="lq-rm__tileValue">
                {assessment.rows_to_change.toLocaleString()}
              </div>
            </div>
            <div className="lq-rm__tile">
              <div className="lq-rm__tileKey">{t('remediation.stat.flagged')}</div>
              <div className="lq-rm__tileValue">
                {t('remediation.stat.findings', { count: flagged })}
              </div>
            </div>
            <div className="lq-rm__tile">
              <div className="lq-rm__tileKey">{t('remediation.stat.rollback')}</div>
              <div
                className={
                  assessment.rollback_strategy === 'none_available'
                    ? 'lq-rm__tileValue lq-rm__unresolved'
                    : 'lq-rm__tileValue'
                }
              >
                {t(`execution.rollbackStrategy.${assessment.rollback_strategy}`)}
              </div>
            </div>
          </div>
          {/* The source moves between an assessment and a decision, so a reader can take the
              measurement again — and only when they ask. */}
          <button
            type="button"
            className="lq-rm__act lq-rm__act--quiet"
            onClick={onAssess}
          >
            {t('remediation.assess.again')}
          </button>
        </>
      )}

      {failure !== null && (
        <Alert tone="error" title={t('remediation.assess.failed')}>
          {failure}
        </Alert>
      )}
    </Card>
  );
}

export function PlanDetail(): ReactElement {
  const { t } = useTranslation();
  const { planId = '' } = useParams();
  const plan = usePlan(planId);
  const assess = useAssessExecution();
  /* The dataset's version history, read only to turn this plan's `dataset_version_id` into an
     ordinal. Enabled once the plan has loaded, because the dataset id comes off it — and a failed
     read is not a failure of this screen: `factsOf` falls back to the id. */
  const versions = useSchemaHistory(plan.data?.dataset_id ?? '');
  const [now] = useState(() => new Date());
  const [done, setDone] = useState<Done | null>(null);

  /* The relation once the plan has loaded, the id until then. A breadcrumb that changed from a
     name to an id on a failed read would read as navigation rather than as a failure. */
  const heading = (plan.isSuccess ? relationOf(plan.data) : null) ?? planId;
  const crumb = (
    <nav className="lq-rm__crumb" aria-label={t('remediation.crumbLabel')}>
      <Link to={SCREEN_PATHS.remediation}>{t('remediation.title')}</Link>
      <span aria-hidden="true">/</span>
      <span className="lq-mono lq-rm__crumbHere">{heading}</span>
    </nav>
  );

  if (plan.isPending) {
    return (
      <div className="lq-rm">
        {crumb}
        <SkeletonRows rows={4} columns={6} />
      </div>
    );
  }
  if (plan.isError) {
    return (
      <div className="lq-rm">
        {crumb}
        <EmptyState
          title={t('remediation.notFound.title')}
          guidance={plan.error.message}
        />
      </div>
    );
  }

  const found = plan.data;
  const relation = relationOf(found);
  const tone = stateToneOf(found.state);
  /* The mutation is a single-slot observer, so `variables` must be checked or a stale assessment
     from another plan would render against this one. */
  const assessment = assess.isSuccess && assess.variables === planId ? assess.data : null;

  return (
    <div className="lq-rm">
      {crumb}

      <div className="lq-rm__head">
        <div className="lq-rm__headText">
          {/* **The table, not the plan's UUID.** A plan is read by somebody deciding whether to
              change a customer's data, and the first thing they need is which table. The id is
              still on the page — the line below and the Plan card — because it is what a support
              conversation and every API call are keyed on. */}
          <h1 className="lq-rm__h1 lq-mono">
            {relation ?? t('remediation.unnamedRelation')}
          </h1>
          <div className="lq-rm__planId lq-mono" title={t('remediation.planIdHint')}>
            {found.id}
          </div>
          <div className="lq-rm__sub">
            {t('remediation.subline', {
              correctable: found.applicable_count,
              flagged: unfixableCount(found),
              mode: t(`remediation.mode.${found.mode}`),
            })}
          </div>
        </div>
        <span className={`lq-tag lq-tag--rm-${tone}`}>
          {t(stateLabelKey(found.state))}
        </span>
      </div>

      <p className={`lq-tintband lq-tintband--${tone}`}>
        {t(`remediation.stateHint.${found.state}`)}
      </p>

      {done === null ? null : (
        <div className="lq-tintband lq-tintband--ok" role="status">
          <div className="lq-tintband__text">
            <strong>{t(`remediation.done.${done.action}.title`)}</strong>{' '}
            {t(`remediation.done.${done.action}.body`)}
          </div>
          <button
            type="button"
            className="lq-tintband__dismiss"
            aria-label={t('remediation.dismiss')}
            onClick={() => {
              setDone(null);
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* **What the signature unlocks, before the field that asks for one.** Rendered only while a
          decision is open: afterwards it would restate the proposals table as history. */}
      {found.state === 'awaiting_approval' && <SigningBrief plan={found} />}

      <PlanActions
        plan={found}
        onDone={(action) => {
          setDone({ action });
        }}
      />

      <AssessmentCard
        plan={found}
        assessment={assessment}
        busy={assess.isPending}
        failure={
          assess.isError && assess.variables === planId ? assess.error.message : null
        }
        onAssess={() => {
          assess.mutate(planId);
        }}
      />

      <ScriptSection plan={found} />

      <Card title={t('remediation.proposals')} table>
        <ProposalsTable plan={found} assessment={assessment} />
      </Card>

      <div className="lq-rm__record">
        <Card title={t('remediation.planPanel')}>
          <dl className="lq-rm__facts">
            {factsOf(found, {
              /* Resolved from the catalogue rather than carried on the plan, because the plan
                 stores the version's id and an ordinal is what a person can compare. `null` while
                 the read is in flight or if the version is gone, which `factsOf` renders as the id
                 rather than as a number nobody measured. */
              versionNumber:
                versions.data?.items.find(
                  (version) => version.id === found.dataset_version_id,
                )?.version_number ?? null,
              executionHref: `${SCREEN_PATHS.executions}/${found.execution_id}`,
            }).map((fact) => (
              <div key={fact.key} className="lq-rm__fact">
                <dt>{t(fact.key)}</dt>
                <FactValue fact={fact} />
              </div>
            ))}
          </dl>
        </Card>

        <ArtifactPanel plan={found} now={now} />

        <Card title={t('remediation.lifecycle')}>
          <dl className="lq-rm__facts">
            {lifecycleOutline(found).map((entry, index) => (
              <div key={`${entry.verbKey}-${String(index)}`} className="lq-rm__fact">
                <dt>{t(entry.verbKey)}</dt>
                {entry.when === null ? (
                  /* A step still ahead of the plan. A dash rather than a blank, so the row reads
                     as "not yet" rather than as a missing value. */
                  <dd className="lq-muted">—</dd>
                ) : (
                  <dd title={asInstant(entry.when)}>
                    {t('remediation.lifeWhen', {
                      age: ageText(t, entry.when, now) ?? asInstant(entry.when),
                      who:
                        entry.who === null
                          ? t('remediation.byPlatform')
                          : personName(entry.who),
                    })}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        </Card>
      </div>
    </div>
  );
}

/** One fact's value: a link, a translated label, or text — with the whole id on hover.
 *
 * A component rather than three branches inline, because the card renders seven of these and the
 * distinction between "this is data" and "this is a sentence standing in for missing data" has to
 * be made identically each time. `muted` is the second: a fallback rendered in the same weight as a
 * measured value is a fallback a reader takes for one.
 */
function FactValue({ fact }: { readonly fact: Fact }): ReactElement {
  const { t } = useTranslation();
  const shown = fact.translate === true ? t(fact.value) : fact.value;
  const className = [fact.mono ? 'lq-mono' : '', fact.muted === true ? 'lq-muted' : '']
    .filter((part) => part !== '')
    .join(' ');

  return (
    <dd className={className === '' ? undefined : className} title={fact.expands}>
      {fact.to !== undefined ? <Link to={fact.to}>{shown}</Link> : shown}
    </dd>
  );
}

/** The plan as sentences an approver can sign, shown while their signature is being asked for.
 *
 * One line per applicable proposal — "and 3 more" is not a thing to sign — each with its measured
 * count under it. What the plan does NOT fix is stated too, because a partial fix presented without
 * its denominator reads as a complete one.
 */
function SigningBrief({ plan }: { readonly plan: Plan }): ReactElement {
  const { t } = useTranslation();
  const flagged = unfixableCount(plan);

  return (
    <section className="lq-cp__card lq-rm__brief" aria-labelledby="rm-signing">
      <h2 id="rm-signing" className="lq-cp__cardTitle">
        {t('remediation.does.title')}
      </h2>
      <ul className="lq-rm__briefList">
        {signingLines(plan).map((line) => (
          <li key={line.id} className="lq-rm__briefItem">
            <span>{t(line.key, line.args)}</span>
            <span className="lq-cp__muted">
              {line.rows === null
                ? t('remediation.does.unsimulated')
                : t('remediation.does.rows', { rows: line.rows })}
            </span>
          </li>
        ))}
      </ul>
      {flagged > 0 && (
        <p className="lq-rm__briefFlagged">
          {t('remediation.does.flagged', { count: flagged })}
        </p>
      )}
    </section>
  );
}

/** The lifecycle controls, offered only where the plan's state admits them.
 *
 * **The steps sit in one row and the decision in its own card**, as the design separates them: a
 * step moves the plan along, and a decision is a signature that needs a reason. A rejection with no
 * stated reason is what an incident review a year later cannot work with, so the two decision buttons
 * stay closed until one is typed rather than sending an empty string the server would refuse.
 *
 * **Simulate reads the connection off the plan's own dataset**, never from a picker. The connection
 * is a fact about where the dataset lives; asking a person to choose one is asking them to be right
 * about something the platform already knows, and a wrong choice would open a read-only session
 * against somebody else's database.
 */
function PlanActions({
  plan,
  onDone,
}: {
  readonly plan: Plan;
  readonly onDone: (action: PlanAction) => void;
}): ReactElement {
  const { t } = useTranslation();
  const act = usePlanAction();
  const dataset = useDataset(plan.dataset_id);
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const hintId = useId();

  const offered = offeredFor(plan);
  const isDecision = (action: PlanAction): boolean =>
    action === 'approve' || action === 'reject';
  const steps = offered.filter((each) => !isDecision(each.action));
  const decides = offered.some((each) => isDecision(each.action));
  /* What to tell this reader about signing, when there is something to say. Rendered rather than
     left as an absence: a plan that says it is waiting on a person above a row of buttons that does
     not include Approve reads as a screen that failed to load one. */
  const notice = decisionNotice(plan);
  const blocked = blockedFor(plan);

  const run = (action: PlanAction): void => {
    act.mutate(
      {
        planId: plan.id,
        action,
        ...(isDecision(action) ? { reason: reason.trim() } : {}),
        ...(action === 'simulate' && dataset.isSuccess
          ? { connectionId: dataset.data.connection_id }
          : {}),
      },
      {
        onSuccess: () => {
          setReason('');
          onDone(action);
        },
      },
    );
  };

  return (
    <>
      {(steps.length > 0 || blocked.length > 0) && (
        <div className="lq-rm__actions">
          {steps.map((each) => {
            /* Simulate cannot be offered until the dataset read has answered, because the
               connection is what the request carries. Disabled with the reason rather than hidden:
               a control that appears a moment later reads as a glitch. */
            const waiting = each.action === 'simulate' && !dataset.isSuccess;
            return (
              <button
                key={each.action}
                type="button"
                className={
                  each.action === 'abandon'
                    ? 'lq-rm__act lq-rm__act--quiet'
                    : 'lq-rm__act lq-rm__act--primary'
                }
                disabled={act.isPending || waiting}
                title={waiting ? t('remediation.awaitingDataset') : undefined}
                onClick={() => {
                  run(each.action);
                }}
              >
                {act.isPending && act.variables.action === each.action
                  ? t(`remediation.busy.${each.action}`)
                  : t(`remediation.action.${each.action}`)}
              </button>
            );
          })}
          {/* The step a reader looks for and cannot take yet: drawn where it would be, as a step
              that is not a control, with the reason said in words beside it rather than only on a
              hover — a touch screen has no hover. */}
          {blocked.map((key) => (
            <span key={key} className="lq-rm__blockedStep">
              <span className="lq-rm__blocked" title={t(`${key}.why`)}>
                {t('remediation.action.emit')}
              </span>
              <span className="lq-rm__blockedWhy">{t(`${key}.why`)}</span>
            </span>
          ))}
        </div>
      )}

      {decides && (
        <section
          className="lq-cp__card lq-rm__decide"
          aria-label={t('remediation.decide')}
        >
          <div className="lq-rm__reason">
            <label htmlFor={reasonId} className="lq-rm__reasonLabel">
              {t('remediation.reasonLabel')}
            </label>
            <textarea
              id={reasonId}
              className="lq-rm__textarea"
              rows={2}
              value={reason}
              aria-describedby={hintId}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <span id={hintId} className="lq-cp__muted">
              {t('remediation.reasonHint')}
            </span>
          </div>
          <div className="lq-rm__decideActions">
            {(['reject', 'approve'] as const).map((action) => (
              <button
                key={action}
                type="button"
                className={
                  action === 'approve'
                    ? 'lq-rm__act lq-rm__act--primary'
                    : 'lq-rm__act lq-rm__act--quiet'
                }
                disabled={act.isPending || reason.trim() === ''}
                onClick={() => {
                  run(action);
                }}
              >
                {t(`remediation.action.${action}`)}
              </button>
            ))}
          </div>
        </section>
      )}

      {notice !== null && (
        /* **Two headings, because they are two different facts.** A signature is a receipt —
           telling somebody who just approved that they "cannot sign this one" presents their own
           success as a rejection. The button is correctly withheld in both cases; only the sentence
           differs. The detail is the server's own sentence, verbatim. */
        <div
          className={`lq-tintband ${
            notice.kind === 'signed' ? 'lq-tintband--ok' : 'lq-tintband--bad'
          }`}
          role="status"
        >
          <div className="lq-tintband__text">
            <strong>
              {notice.kind === 'signed'
                ? t('remediation.youSigned')
                : t('remediation.notYours')}
            </strong>{' '}
            {notice.detail}
          </div>
        </div>
      )}

      {act.isError && (
        <Alert tone="error" title={t('remediation.actionFailed')}>
          {act.error.message}
        </Alert>
      )}
      {dataset.isError && (
        /* Simulate is disabled above, and this says why rather than leaving a dead control. */
        <Alert tone="warning">{t('remediation.datasetUnreadable')}</Alert>
      )}
    </>
  );
}

function ArtifactPanel({
  plan,
  now,
}: {
  readonly plan: Plan;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  /* Only asked for once a plan has emitted something. A plan that never has would answer with an
     empty list, which is honest and is still a round trip per page view for a card whose answer
     the plan's own state already gives. */
  const artifacts = usePlanArtifacts(plan.id, hasArtifact(plan));

  return (
    <Card title={t('remediation.artifact')}>
      {!hasArtifact(plan) && (
        <p className="lq-rm__line lq-muted">{t('remediation.artifactAbsent')}</p>
      )}
      {artifacts.isPending && hasArtifact(plan) && <SkeletonRows rows={2} />}
      {artifacts.isError && (
        <Alert tone="error" title={t('remediation.artifactFailed')}>
          {artifacts.error.message}
        </Alert>
      )}
      {artifacts.isSuccess && artifacts.data.length === 0 && (
        /* The state says emitted and the store holds nothing. Reported rather than rendered as an
           empty card, because those are different facts and one of them is a problem. */
        <p className="lq-rm__absent">{t('remediation.artifactMissing')}</p>
      )}
      {artifacts.isSuccess &&
        artifacts.data.map((artifact) => (
          <div key={artifact.checksum} className="lq-rm__artifact">
            <div className="lq-mono lq-rm__artifactName">{artifact.filename}</div>
            <div className="lq-cp__muted">
              <span className="lq-mono" title={artifact.checksum}>
                {artifact.checksum.slice(0, 16)}
              </span>
              {' · '}
              <span title={asInstant(artifact.emitted_at)}>
                {ageText(t, artifact.emitted_at, now) ?? asInstant(artifact.emitted_at)}
              </span>
            </div>
            <ArtifactDownload planId={plan.id} filename={artifact.filename} />
          </div>
        ))}
    </Card>
  );
}

/** The control that fetches an emitted script.
 *
 * **A button rather than a link, and that is the fix.** `<a href>` navigates, and a navigation
 * carries no bearer token — so the route answered 401 and replaced the app with the API's problem
 * document. `downloadFile` fetches it as the signed-in user and hands the bytes to the browser.
 *
 * Held in its own component so the pending flag belongs to one artifact: a card-level flag would
 * disable every row's control while one of them was in flight, and a plan can hold more than one
 * emission.
 */
function ArtifactDownload({
  planId,
  filename,
}: {
  readonly planId: string;
  readonly filename: string;
}): ReactElement {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function save(): Promise<void> {
    setSaving(true);
    setFailure(null);
    try {
      /* The server names the file in `Content-Disposition`; the listing's own name is the fallback,
         so the two cannot disagree about which artifact was handed over. */
      await downloadFile(artifactUrl(planId), filename);
    } catch (error) {
      /* The message is the server's problem detail, which distinguishes a permission refusal from a
         plan nobody has emitted. Reported rather than swallowed: a download that quietly does
         nothing is indistinguishable from one the browser is still working on. */
      setFailure(
        error instanceof Error ? error.message : t('remediation.downloadFailed'),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="lq-rm__act lq-rm__act--small"
        disabled={saving}
        onClick={() => {
          void save();
        }}
      >
        {saving ? t('remediation.downloading') : t('remediation.download')}
      </button>
      {failure !== null && (
        <Alert tone="error" title={t('remediation.downloadFailed')}>
          {failure}
        </Alert>
      )}
    </>
  );
}

/** The script this plan would emit, rendered on request and never on mount.
 *
 * **This closes an ordering problem rather than adding a convenience.** Emission requires two
 * signatures, so without it the two people signing for a change to a customer's production data
 * could not read the SQL until after they had signed for it. `SigningBrief` above is the plan as
 * sentences and this is the plan as statements, and an approver wants both.
 *
 * **Behind a control**, because handing over a script should be something somebody asked for —
 * the sibling `/artifacts` route withholds script content for that exact reason, so fetching it on
 * every page render would defeat the rule one route over. Hiding it again keeps the read: the
 * preview is cached under the plan, so showing it a second time does not ask again.
 *
 * **No download and no copy button, deliberately.** The flow is read, approve, then take the file —
 * and the file is what carries the checksum somebody signed. A copy control here would be a quiet
 * way around the approval this section exists to inform, on text whose own header says not to run
 * it.
 */
function ScriptSection({ plan }: { readonly plan: Plan }): ReactElement {
  const { t } = useTranslation();
  /* Local state rather than a URL parameter: whether somebody has opened the script is not a fact
     about the plan, and putting it in the address would make a shared link fetch a script for
     whoever opened it. `asked` stays true once set, so the cached preview is reused. */
  const [asked, setAsked] = useState(false);
  const [open, setOpen] = useState(false);
  const preview = useScriptPreview(plan.id, asked);

  const control = (
    <button
      type="button"
      className="lq-cp__headButton"
      aria-expanded={open}
      onClick={() => {
        setAsked(true);
        setOpen(!open);
      }}
    >
      {open ? t('remediation.script.hide') : t('remediation.script.show')}
    </button>
  );

  return (
    <Card title={t('remediation.script.title')} action={control}>
      {/* A failed render is reported as a failure of the request, never as an absence of a script.
          The server's own `detail` carries the reason — an advise-mode plan emits nothing to run,
          an abandoned plan can never be approved. */}
      {open && preview.isError && (
        <Alert tone="error" title={t('remediation.script.failed')}>
          {preview.error.message}
        </Alert>
      )}
      {open && preview.isPending && (
        <p className="lq-cp__muted">{t('remediation.script.loading')}</p>
      )}

      {open && preview.isSuccess && (
        <>
          {/* **Keyed on `approved` from the payload, never on the plan's state.** `emitted` and
              `executed` are both past approval, so a check against one state name would stamp "do
              not run this" on a script somebody had already signed. */}
          {preview.data.approved ? null : (
            <div className="lq-tintband lq-tintband--warn">
              <div className="lq-tintband__text">
                <strong>{t('remediation.script.unapproved')}</strong>{' '}
                {t('remediation.script.unapprovedRest')}
              </div>
            </div>
          )}
          {/* `<pre>` with the content as a child, so nothing interprets it. */}
          <pre className="lq-mono lq-rm__scriptBody">{preview.data.content}</pre>
          <p className="lq-cp__muted">
            {preview.data.approved
              ? t('remediation.script.approvedNote', { filename: preview.data.filename })
              : t('remediation.script.unapprovedNote', {
                  filename: preview.data.filename,
                })}
          </p>
        </>
      )}
    </Card>
  );
}
